import { useStore } from '@nanostores/react';
import { motion, useAnimationControls, useIsomorphicLayoutEffect, type MotionStyle } from 'framer-motion';
import { useRef } from 'react';
import { themeStore } from '~/lib/stores/theme';
import { classNames } from '~/utils/classNames';

interface CounterProps {
  className?: string;
  onChange?: (previousValue: number) => void;
  children: number;
  overflow?: number;
}

const STYLES_RESET = {
  scale: 1,
  background: `var(--bolt-elements-messages-counter-background)`,
} satisfies MotionStyle;

export function Counter({ children: value, className, onChange, overflow }: CounterProps) {
  const controls = useAnimationControls();
  const previousValue = useRef<number | null>(null);
  const theme = useStore(themeStore);

  useIsomorphicLayoutEffect(() => {
    if (previousValue.current == null || previousValue.current === value) {
      previousValue.current = value;
      return undefined;
    }

    onChange?.(previousValue.current);

    previousValue.current = value;

    controls.set(STYLES_RESET);

    controls
      .start({ scale: 1.25, background: `var(--bolt-elements-messages-counter-backgroundPulse)` }, { duration: 0.05 })
      .then(async () => {
        if (!stopped) {
          await controls.start(STYLES_RESET, { duration: 0.2 });
        }
      });

    let stopped = false;

    return () => {
      stopped = true;
      controls.stop();
    };
  }, [value, theme]);

  return (
    <motion.div
      className={classNames(
        'flex shrink-0 leading-6 justify-center items-center rounded-full w-6 h-6',
        value >= 100 ? 'text-[10px]' : 'text-xs',
        className,
      )}
      style={STYLES_RESET}
      animate={controls}
    >
      {overflow && value > overflow ? `${overflow}+` : value}
    </motion.div>
  );
}
